<template>
  <Slide
    :is-open="isOpened"
    no-overlay
    slide
    data-testid="slideMenu"
    :close-on-navigation="true"
    @open-menu="onOpenMenu"
    @close-menu="onCloseMenu"
  >
    <div class="block-logo">
      <a :href="links.home">
        <img
          alt="Ankorstore"
          class="logoMenu"
          :src="getStaticAssetUrl('/images/logo/logo-black.svg')"
          importance="high"
        >
      </a>
    </div>
    <div v-if="!userIsBrand">
      <div v-if="user">
        <div class="dropdown-divider" />
        <h6 class="dropdown-header">
          {{ $t('Hello {Name}', { Name: user.first_name }) }}
        </h6>
      </div>
      <RetailInformationMenuAccount v-if="userIsRetailer" />
      <div class="dropdown-divider" />
      <div
        v-if="userIsRetailer && showCreditPillSlot"
        data-testid="CreditLimitPillMobileContainer"
      >
        <div class="credit-limit-pill-mobile">
          <AkParagraph size="sm">
            {{ $t('retailer.header.creditLimit.description') }}
          </AkParagraph>
          <CreditLimitPill :is-visible="!isDesktopHeaderViewport" />
        </div>
        <div class="dropdown-divider" />
      </div>
      <div
        v-if="userIsRetailer && showPromoPill"
        data-testid="MobilePromoPillContainer"
      >
        <div class="mobile-promo-pill-container">
          <MobilePromoPill :content="promoPillContent" />
        </div>
        <div class="dropdown-divider" />
      </div>
      <div id="navbarSupportedContent">
        <ul
          v-if="navigationItems"
          class="main-nav"
        >
          <li v-if="$isEnabled('discount_in_navbar')">
            <a :href="links.discounts">{{ $t('Discount Title') }}</a>
          </li>
          <AkNavigation
            v-if="isOpened"
            :use-router-link="isRouterLink"
            is-mobile
            :items="navigationItems"
            @item-clicked="trackNavigation"
          >
            <template #rightSectionBanner="{ item }">
              <NavItemBanner
                v-if="item.bannerData && $isEnabled('fresh-products-banner')"
                :banner-data="item.bannerData || null"
              />
            </template>
          </AkNavigation>
        </ul>
        <ul
          v-if="!user"
          class="user-nav"
        >
          <li class="divider" />
          <li>
            <a
              href=""
              @click.prevent="openAuthPopin(AuthFormType.Login)"
            >{{ $t('Login') }}</a>
          </li>
          <li>
            <a
              href="#"
              @click.prevent="openAuthPopin(RetailerFormType.Register)"
            >{{ registerCopyright }}</a>
          </li>
          <li>
            <a
              href="#"
              @click.prevent="onApplyAsBrand"
            >{{ $t('Apply as a brand') }}</a>
          </li>
        </ul>
        <div
          v-if="userIsRetailer"
          class="dropdown-divider"
        />
        <MenuMobileRetailer
          v-if="userIsRetailer"
          data-testid="menuMobileRetailer"
        />
      </div>
    </div>
    <MobileNavigation
      v-if="userIsBrand"
      :brand="brand"
      :navigation-items="navigationItems"
      :opened="isOpened"
    />
  </Slide>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Slide } from 'vue3-burger-menu';
import { mapGetters } from 'vuex';
import { openBrandRegistrationEntry } from '@monolith/legacy/services/signup/brand-registration-entry';
import { useAuthPopin } from '@core/composables/use-auth-popin';
import MenuMobileRetailer from '@monolith/legacy/components/header/menu-mobile-retailer.vue';
import RetailInformationMenuAccount from '@monolith/legacy/components/header/retail-information-menu-account.vue';
import { FeatureFlag } from '@monolith/legacy/services/features';
import { RetailerFormType } from '@monolith/legacy/types/retailer-form-type';
import { AuthFormType } from '@monolith/legacy/types/auth-form-type';
import { AkNavigation, AkNavMenuExtended } from '@ankorstore/design-system';
import { NavigationMenuService, prepareDataForAkNavBar, TrackingService } from '@bc/discovery';
import MobileNavigation from '@modules/shared/components/header/mobile-navigation/mobile-navigation.vue';
import CreditLimitPill from '@monolith/legacy/components/header/credit-limit-pill.vue';
import MobilePromoPill from '@monolith/legacy/components/header/mobile-promo-pill.vue';
import NavItemBanner from '@bc/discovery/feature/navigation-menu/components/NavItemBanner.vue';
import { useCreditLimit } from '@modules/account/retailer-settings/composables/use-credit-limit';
import { getCmsHeaderPills } from '@monolith/legacy/modules/promote-engage/services/pill';
import type { HeaderPill } from '@monolith/legacy/types/api/pill';
import { getStaticAssetUrl } from '@modules/shared/util/img-cdn/img-cdn';
import { useMediaQuery } from '@vueuse/core';

export default defineComponent({
  name: 'HeaderMobile',
  components: {
    Slide,
    MenuMobileRetailer,
    RetailInformationMenuAccount,
    AkNavigation,
    MobileNavigation,
    CreditLimitPill,
    MobilePromoPill,
    NavItemBanner,
  },
  props: {
    links: { type: Object, required: true },
    showAnkorRdv: { type: Boolean, required: false, default: false },
  },
  setup() {
    const { showCreditLimitPill, isBusinessVerificationPending, setCreditLimitFromCms } = useCreditLimit();
    const { openAuthPopin } = useAuthPopin();

    // Mirrors #headerMobile's `media-breakpoint-up(xl)` CSS rule (xl = 1280px), same breakpoint
    // header-pill.vue uses for #header — so CreditLimitPill's impression tracking doesn't
    // double-count across both CSS-toggled mounts (this container is hidden at/above xl).
    const isDesktopHeaderViewport = useMediaQuery('(min-width: 1280px)');

    return {
      showCreditLimitPill,
      isBusinessVerificationPending,
      setCreditLimitFromCms,
      openAuthPopin,
      isDesktopHeaderViewport,
    };
  },
  data() {
    return {
      navigationItems: [],
      opened: false,
      link: '',
      RetailerFormType,
      AuthFormType,
      promoPillContent: null as HeaderPill | null,
    };
  },
  computed: {
    ...mapGetters(['userIsRetailer', 'userIsBrand', 'user']),
    ...mapGetters(['brand']),
    showPromoPill(): boolean {
      return !!this.promoPillContent;
    },
    // The verification pill (RCN-498) occupies the same mobile slot as the credit limit pill and
    // takes precedence over it — see use-credit-limit-pill.ts for why they're mutually exclusive.
    showCreditPillSlot(): boolean {
      return this.showCreditLimitPill || this.isBusinessVerificationPending;
    },
    isRouterLink() {
      if (!this.$router || !this.$route || !this.$route.path) {
        return false;
      }
      const currentPath = this.$route.path;
      const resolvedPath = this.$router?.resolve(currentPath);
      return resolvedPath?.route?.path !== '*';
    },
    registerCopyright(): string {
      return this.$isEnabled(FeatureFlag.RegistrationRename) ? this.$t('Join as a retailer') : this.$t('Register');
    },
    isOpened(): boolean {
      return this.opened;
    },
  },
  watch: {
    $route() {
      this.onCloseMenu();
    },
  },
  async mounted() {
    // Desktop hides the mobile header, so its menu can wait until it is actually opened.
    const isVisible = this.$el.getClientRects().length > 0;
    const [, cmsData] = await Promise.all([
      isVisible ? this.loadNavigationItems() : Promise.resolve(),
      this.userIsRetailer ? getCmsHeaderPills() : Promise.resolve(null),
    ]);
    if (cmsData) {
      this.setCreditLimitFromCms(cmsData.credit_limit, cmsData.business_verification);
      this.promoPillContent = cmsData.pill ?? null;
    }
  },
  methods: {
    onApplyAsBrand(): Promise<void> {
      return openBrandRegistrationEntry(this.openAuthPopin);
    },
    onCloseMenu(): void {
      this.$el.classList.remove('menu-mobile--open');
      document.body.classList.remove('noScroll');
      this.opened = false;
    },
    onOpenMenu(): void {
      this.$el.classList.add('menu-mobile--open');
      document.body.classList.add('noScroll');
      this.opened = true;
      void this.loadNavigationItems();
    },
    async loadNavigationItems(): Promise<void> {
      if (this.navigationItems.length) {
        return;
      }
      const data = await NavigationMenuService.getMainNavigationMenu();
      if (data) {
        this.navigationItems = prepareDataForAkNavBar(data);
      }
    },
    trackNavigation(item: AkNavMenuExtended): void {
      TrackingService.trackNavigation(item);
    },
    getStaticAssetUrl,
  },
});
</script>

<style lang="scss">
@import '@css/vue-import';

.burgerMenu {
  .block-logo {
    line-height: 48px;
    display: flex !important;
    justify-content: center;
    min-height: 60px;
    align-items: center;

    img.logoMenu {
      width: 190px;
    }
  }

  .bm-burger-button {
    @apply ds-relative ds-top-0 ds-left-0 ds-cursor-pointer;
    z-index: 1;
    width: 20px;
    height: 14px;
  }

  .bm-burger-bars {
    @apply ds-bg-primary;
  }

  .line-style {
    position: absolute;
    height: 8%;
    left: 0;
    right: 0;
  }

  .cross-style {
    position: absolute;
    top: 10px;
    right: 4px;
    cursor: pointer;
  }

  .bm-cross {
    @apply ds-bg-neutral-500;
    top: 14px;
    left: 19px;
  }

  .bm-cross-button {
    height: 40px;
    width: 40px;
  }

  .bm-menu {
    @apply ds-font-basic;
    height: 100vh; /* 100% Full-height */
    width: 0; /* 0 width - change this with JavaScript */
    position: fixed; /* Stay in place */
    z-index: 1000; /* Stay on top */
    top: 0;
    left: 0;
    right: 0;
    opacity: 0;
    @apply ds-bg-white;
    overflow-x: hidden; /* Disable horizontal scroll */
    overflow-y: scroll;
    padding-top: 0; /* Place content 60px from the top */
    transition: 0.5s; /*0.5 second transition effect to slide in the sidenav*/
    box-shadow:
      8px 0 8px -10px black,
      -8px 0 8px -10px black;
  }
}

.bm-overlay {
  background: rgba(0, 0, 0, 0.3);
}

nav.bm-item-list {
  @apply ds-text-primary;
  margin-left: 0%;
  font-size: 14px;

  & > * {
    display: block;
    text-decoration: none;
    @apply ds-w-screen;

    & > span {
      margin-left: 10px;
      font-weight: 700;
      @apply ds-text-primary;
    }
  }
}

// Lift days are enabled via FF and only part of the year
.lift-days-title {
  @apply ds-text-discount-700;
  white-space: pre-wrap;
  font-weight: 700;
}

.lift-days-subtitle {
  @apply ds-text-primary;
  font-weight: 400;
  text-decoration: underline;
}

#headerMobile #navbarSupportedContent {
  position: relative;
  width: 100%;
  padding: 0;
  overflow: auto;
  flex-shrink: 1;
  padding-bottom: 113px;

  .ak-account-nav__section-header {
    @apply ds-py-2 ds-px-2;
  }

  ul {
    margin: 0;

    > li:not(.ak-nav-item):not([class*='ak-account-nav']) {
      display: block;
      width: 100%;
      height: 40px;
      line-height: 40px;
      padding: 0 10px;
      @apply ds-text-primary;
      align-items: center;

      &#liftBrandPage {
        a {
          display: flex;
          align-items: center;

          img {
            width: 24px;
            height: auto;
            margin-right: 8px;
          }
        }
      }

      &#lift-days-item,
      &#ankorstore-rdv-item {
        display: flex;
      }

      &.header {
        @apply ds-border-b ds-border-solid ds-border-neutral-300 ds-text-neutral-500 ds-font-basic;
        text-align: center;
      }

      &.divider {
        @apply ds-border-b ds-border-solid ds-border-neutral-300;
        height: 0;
      }

      a {
        @apply ds-flex ds-items-center ds-pl-2;
        text-decoration: none;

        &.disabled {
          @apply ds-text-neutral-600 ds-cursor-not-allowed;
        }

        .ak-badge {
          top: 3px;
        }

        .menu-badge--new {
          @apply ds-ml-2;
        }
      }

      img {
        height: 28px;
        display: block;
      }
    }
  }
}

.menu-mobile--open {
  .bm-menu {
    width: 100% !important;
    opacity: 1;
  }
}

.credit-limit-pill-mobile {
  @apply ds-flex ds-justify-between ds-items-center ds-my-4;
  padding: 0 18px;
}

.mobile-promo-pill-container {
  @apply ds-my-4;
}
</style>
